import type { InferType, Selection } from 'groqd'
import { q } from 'groqd'

import {
  borderRadiusEnum,
  borderRadiusUnion,
  codedSectionUnion,
  colorEnum,
  colorUnion,
  flexibleImage,
  horizontalAlignmentUnion,
  imageAsset,
  spaceEnum,
  spaceUnion,
  verticalAlignmentUnion,
} from './helperTypes'
import { imageSchema } from './image'

const columnsQuery = (name: string) =>
  q(`coalesce(${name}[] {
    _type == 'reference' => @->,
    _type != 'reference' => @
  }, [])`)
    .filter()
    .grab({
      _id: q.string().nullable(),
      _key: q.string(),
      _type: q.string(),
      backgroundColor: colorUnion.nullable(),
      backgroundImage: q('backgroundImage')
        .grab({
          _type: q.literal('image'),
          fit: q
            .union([
              q.literal('scale-down'),
              q.literal('contain'),
              q.literal('cover'),
              q.literal('fill'),
            ])
            .nullable(),
          asset: q('asset').deref().grab(imageAsset).nullable(),
        })
        .nullable(),
      alignHorizontally: horizontalAlignmentUnion.nullable(),
      alignVertically: verticalAlignmentUnion.nullable(),
      alignBackground: q.union([horizontalAlignmentUnion, verticalAlignmentUnion]).nullable(),
      space: spaceUnion.nullable(),
      fullSizeImage: q('fullSizeImage').grab(flexibleImage).nullable(),
      fullSizeImageSlideToEdge: q.boolean().nullable(),
      fullSizeImageAlignment: q
        .union([q.literal('top'), q.literal('bottom'), q.literal('center'), q.literal('full')])
        .nullable(),
      content: ['content[$lang]', q.array(q.unknown()).nullable()],
    })

const sharedSectionSelection = {
  _type: q.literal('website.richContent.sharedSection'),
  _key: ['_id', q.string()],
  name: ['name.current', q.string()],
  reverseColumnOrderForWideScreen: q.boolean().nullable(),
  verticalPadding: spaceUnion.nullable(),
  columns: columnsQuery('columns'),
} satisfies Selection

const headerSelection = {
  _type: q.literal('website.richContent.headerSection'),
  name: ['name.current', q.string().nullable()],
  overline: ['overline[$lang]', q.string()],
  productTitle: ['productTitle[$lang]', q.string()],
  productDescription: ['productDescription[$lang]', q.string()],
  flowName: q.string(),
  ctaSupportText: ['ctaSupportText[$lang]', q.string()],
  ctaLabel: ['ctaLabel[$lang]', q.string()],
  colorTheme: q.string(),
  backgroundImage: q('backgroundImage')
    .grab({
      _type: q.literal('image'),
      asset: q('asset').deref().grab(imageAsset).nullable(),
    })
    .nullable(),
  reverseColumnOrderForWideScreen: q.boolean().nullable(),
  verticalPadding: spaceUnion.nullable(),
  columns: columnsQuery('columns'),
} satisfies Selection

export const multiColumnSectionSelection = {
  _key: ['coalesce(_key, id, "")', q.string()],
  _type: q.union([
    q.literal('website.richContent.multiColumnSection'),
    q.literal('website.richContent.reference'),
    q.literal('website.richContent.sharedSection'),
    q.literal('website.richContent.headerSection'),
  ]),
  name: ['name.current', q.string().nullable()],
  reverseColumnOrderForWideScreen: q.boolean().nullable(),
  verticalPadding: spaceUnion.nullable(),
  columns: columnsQuery('columns'),
} satisfies Selection

const codedSectionSelection = {
  _key: q.string(),
  _type: q.literal('website.richContent.codedSection'),
  type: codedSectionUnion.nullable(),
} satisfies Selection

const sharedSectionAbTestSelection = {
  _type: q.literal('website.richContent.sharedSection.ab.test'),
  _id: q.string(),
  title: q.string(),
  featureFlag: q.string(),
  variations: q.array(
    q.object({
      name: q.string(),
      section: q.object({
        _type: q.literal('reference'),
        _ref: q.string(),
      }),
    })
  ),
}

const columnBaseSelection = {
  backgroundColor: colorUnion.nullable(),
  backgroundImage: q('backgroundImage')
    .grab({
      _type: q.literal('image'),
      fit: q
        .union([
          q.literal('scale-down'),
          q.literal('contain'),
          q.literal('cover'),
          q.literal('fill'),
        ])
        .nullable(),
      imagePosition: q
        .union([
          q.literal('center center'),
          q.literal('left top'),
          q.literal('center top'),
          q.literal('right top'),
          q.literal('left center'),
          q.literal('right center'),
          q.literal('left bottom'),
          q.literal('center bottom'),
          q.literal('right bottom'),
        ])
        .nullable(),
      asset: q('asset').deref().grab(imageAsset).nullable(),
    })
    .nullable(),
  alignHorizontally: horizontalAlignmentUnion.nullable(),
  alignVertically: verticalAlignmentUnion.nullable(),
  space: spaceUnion.nullable(),
  verticalPadding: spaceUnion.nullable(),
  horizontalPadding: spaceUnion.nullable(),
  borderRadius: borderRadiusUnion.nullable(),
  borderColor: colorUnion.nullable(),
  fullHeight: q.boolean().nullable(),
  content: ['content[$lang]', q.array(q.unknown()).nullable()],
} satisfies Selection

const sectionResponsiveSelection = {
  style: q('style')
    .deref()
    .grab({
      numberOfColumns: q.number(),
      columnsEqualWidth: q.boolean().nullable(),
      columnsLayout: q.array(q.number()).nullable(),
      fullWidth: q.boolean().nullable(),
      verticalPadding: spaceUnion.nullable(),
      horizontalPadding: spaceUnion.nullable(),
      alignHorizontally: horizontalAlignmentUnion.nullable(),
      alignVertically: verticalAlignmentUnion.nullable(),
      backgroundColor: colorUnion.nullable(),
      backgroundImage: q('backgroundImage')
        .grab({
          _type: q.literal('image'),
          fit: q
            .union([
              q.literal('scale-down'),
              q.literal('contain'),
              q.literal('cover'),
              q.literal('fill'),
            ])
            .nullable(),
          asset: q('asset').deref().grab(imageAsset).nullable(),
        })
        .nullable(),
      borderRadius: borderRadiusUnion.nullable(),
      borderColor: colorUnion.nullable(),
      fullWidthBackground: q.boolean().nullable(),
      maxContentWidth: q.number().nullable(),
      columnGap: spaceUnion.nullable(),
      marginBottom: spaceUnion.nullable(),
      marginTop: spaceUnion.nullable(),
    }),
  columns: q('columns')
    .filter()
    .deref()
    .grab({
      _id: q.string().nullable(),
      _type: q.literal('website.richContent.sectionColumn'),
      name: q.slug('name'),
      base: q('base').grab(columnBaseSelection),
      sm: q('sm').filter().grab(columnBaseSelection).slice(0).nullable(),
      md: q('md').filter().grab(columnBaseSelection).slice(0).nullable(),
      lg: q('lg').filter().grab(columnBaseSelection).slice(0).nullable(),
      xl: q('xl').filter().grab(columnBaseSelection).slice(0).nullable(),
      xxl: q('xxl').filter().grab(columnBaseSelection).slice(0).nullable(),
    }),
} satisfies Selection

const localizedMedia = q('media[$lang]').grab({
  typeOfMedia: q.union([q.literal('image'), q.literal('video')]),
  image: imageSchema.optional().nullable(),
  video: q.unknown().optional().nullable(),
  thumbnail: imageSchema.nullable(),
})

const localizedInlineColumns = q('inlineColumns[$lang]').grab({
  _type: q.literal('inlineColumns'),
  columns: q.array(q.unknown()),
})

const sectionSelection = {
  _type: q.literal('website.richContent.section'),
  _id: q.string(),
  name: q.string(),
  base: q('base').grab(sectionResponsiveSelection),
  sm: q('sm').filter().grab(sectionResponsiveSelection).slice(0).nullable(),
  md: q('md').filter().grab(sectionResponsiveSelection).slice(0).nullable(),
  lg: q('lg').filter().grab(sectionResponsiveSelection).slice(0).nullable(),
  xl: q('xl').filter().grab(sectionResponsiveSelection).slice(0).nullable(),
  xxl: q('xxl').filter().grab(sectionResponsiveSelection).slice(0).nullable(),
}

const coverTemplateSelection = {
  _type: q.literal('templates.cover'),
  heading: q('heading').filter('_key == $lang').slice(0).grabOne('value', q.string()),
  subheading: q('subheading')
    .filter('_key == $lang')
    .slice(0)
    .grabOne('value', q.string().nullable()),
  media: localizedMedia,
  inlineColumns: localizedInlineColumns,
}
const headerTemplateSelection = {
  _type: q.literal('templates.header'),
  heading: q('heading').filter('_key == $lang').slice(0).grabOne('value', q.string()),
  subheading: q('subheading')
    .filter('_key == $lang')
    .slice(0)
    .grabOne('value', q.string().nullable()),
  media: localizedMedia,
}

const uspTemplateSelection = {
  _type: q.literal('templates.usp'),
  heading: q('heading').filter('_key == $lang').slice(0).grabOne('value', q.string()),
  subheading: q('subheading')
    .filter('_key == $lang')
    .slice(0)
    .grabOne('value', q.string().nullable()),
  media: localizedMedia,
}

const productsCarouselTemplateSelection = {
  _type: q.literal('templates.productsCarousel'),
  heading: q('heading').filter('_key == $lang').slice(0).grabOne('value', q.string()),
  subheading: q('subheading')
    .filter('_key == $lang')
    .slice(0)
    .grabOne('value', q.string().nullable()),
  carousel: q.unknown(),
}

const localizedAccordionList = q('accordion[$lang]').grab({
  _type: q.literal('accordionList'),
  items: q.unknown(),
  borderColor: colorEnum.nullable(),
  backgroundColor: colorEnum.nullable(),
  fullContainerWidth: q.boolean().nullable(),
  borderRadius: borderRadiusEnum.nullable(),
  padding: spaceEnum.nullable(),
  rowDividerBordered: q.boolean().nullable(),
})

const faqTemplateSelection = {
  _type: q.literal('templates.faq'),
  heading: q('heading').filter('_key == $lang').slice(0).grabOne('value', q.string()),
  subheading: q('subheading')
    .filter('_key == $lang')
    .slice(0)
    .grabOne('value', q.string().nullable()),
  accordion: localizedAccordionList,
}

const sectionsSelection = {
  "_type == 'website.richContent.sharedSection.ab.test'": sharedSectionAbTestSelection,
  "_type == 'website.richContent.codedSection'": codedSectionSelection,
  "_type == 'website.richContent.section'": sectionSelection,
  "_type == 'website.richContent.headerSection'": headerSelection,
  "_type == 'templates.header'": headerTemplateSelection,
  "_type == 'templates.cover'": coverTemplateSelection,
  "_type == 'templates.usp'": uspTemplateSelection,
  "_type == 'templates.productsCarousel'": productsCarouselTemplateSelection,
  "_type == 'templates.faq'": faqTemplateSelection,
  default: multiColumnSectionSelection,
} satisfies Record<string, Selection>

export const sectionABTestVariantQuery = q('*')
  .filter(
    ` (_type == "website.richContent.section" || _type == "website.richContent.sharedSection") && _id == $id`
  )
  .grab(
    {
      _key: ['_id', q.string()],
    },
    {
      '_type == "website.richContent.sharedSection"': sharedSectionSelection,
      '_type == "website.richContent.section"': sectionSelection,
    }
  )
  .slice(0)

export const sectionsQuery = (name: string) =>
  q(`coalesce(${name}[] {
    _type == 'reference' => @->,
    _type != 'reference' => @
  }, [])`)
    .filter()
    .select(sectionsSelection)

export type Sections = InferType<ReturnType<typeof sectionsQuery>>
export type Section = Sections[number]
